import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../App';
import { Link, useLocation } from 'react-router-dom';
import { Home, Box, Users, ShoppingCart, BookOpen, Wrench, UserCog, LogOut, HelpCircle, Menu, X, Bot, ContactRound, BarChart3, Settings, Headphones, Truck, GraduationCap, FileText, Sliders, ClipboardCheck, Calculator } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children, onRestartTour }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [parentInfo, setParentInfo] = useState(null);

  // Load parent info for hierarchical display
  useEffect(() => {
    const loadParentInfo = async () => {
      if (user && user.parent_id && (user.role === 'gestionnaire' || user.role === 'operateur')) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.parent_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const parent = await response.json();
            setParentInfo(parent);
          }
        } catch (error) {
          console.error('Failed to load parent info:', error);
        }
      }
    };
    loadParentInfo();
  }, [user]);

  const navigation = [
    // ===== TOUS =====
    { name: 'Tableau de bord', href: '/', icon: Home, roles: ['groupe', 'licencie', 'gestionnaire', 'operateur', 'logistique'] },
    { name: 'Machines', href: '/machines', icon: Box, roles: ['groupe', 'licencie', 'gestionnaire', 'operateur', 'logistique'] },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['groupe', 'licencie', 'gestionnaire'] },
    { name: 'Classements', href: '/leaderboard', icon: Users, roles: ['groupe', 'licencie', 'gestionnaire'] },
    { name: 'Opportunités', href: '/leads', icon: Users, roles: ['groupe', 'licencie', 'gestionnaire'] },
    { name: 'Étude de Faisabilité', href: '/feasibility-study', icon: ClipboardCheck, roles: ['groupe', 'licencie', 'gestionnaire'] },
    { name: 'Simulateur', href: '/simulator', icon: Calculator, roles: ['groupe', 'licencie', 'gestionnaire'] },
    { name: 'Annuaire', href: '/annuaire', icon: ContactRound, roles: ['groupe', 'licencie', 'gestionnaire'] },
    
    // ===== OPERATEUR ONLY =====
    { name: 'Ma Tournée', href: '/operator-tour', icon: Truck, roles: ['operateur'] },
    
    // ===== GESTIONNAIRE & LICENCIE =====
    { name: 'Boutique', href: '/shop', icon: ShoppingCart, roles: ['licencie', 'gestionnaire'] },
    { name: 'Richard IA', href: '/richard', icon: Bot, roles: ['licencie', 'gestionnaire'] },
    
    // ===== COMMON =====
    { name: 'Base de connaissances', href: '/knowledge', icon: BookOpen, roles: ['groupe', 'licencie', 'gestionnaire', 'operateur'] },
    { name: 'Maintenance', href: '/maintenance', icon: Wrench, roles: ['groupe', 'licencie', 'gestionnaire'] },
    
    // ===== SUPPORT =====
    { name: 'Support', href: '/support', icon: Headphones, roles: ['groupe', 'licencie', 'gestionnaire', 'logistique'] },
    
    // ===== GROUPE ONLY (Pilotage du groupe Noodly) =====
    { name: 'Logistique', href: '/logistics', icon: Truck, roles: ['groupe', 'logistique'] },
    { name: 'Formations', href: '/training', icon: GraduationCap, roles: ['groupe'] },
    { name: 'Formulaires', href: '/forms', icon: FileText, roles: ['groupe'] },
    { name: 'Pilotage Groupe', href: '/admin', icon: Settings, roles: ['groupe'] },
    { name: 'Configurations', href: '/config', icon: Sliders, roles: ['groupe'] },
    { name: 'Licences', href: '/franchises', icon: Home, roles: ['groupe'] },
    { name: 'Utilisateurs', href: '/users', icon: UserCog, roles: ['groupe'] },
  ];

  const filteredNav = navigation.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-black flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/5 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <img 
                src="/images/noodly-logo.png" 
                alt="Noodly" 
                className="h-8 w-auto"
              />
              <h2 className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Pilote</h2>
            </div>
            {parentInfo && user.role === 'gestionnaire' && (
              <p className="text-xs text-gray-400 mt-1">
                Rattaché au licencié : <span className="text-white font-medium">{parentInfo.name}</span>
              </p>
            )}
            {parentInfo && user.role === 'operateur' && (
              <p className="text-xs text-gray-400 mt-1">
                Rattaché au gestionnaire : <span className="text-white font-medium">{parentInfo.name}</span>
              </p>
            )}
          </div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-2"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed md:relative
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        transition-transform duration-300 ease-in-out
        top-0 left-0 bottom-0
        w-64 glass-panel border-r border-white/5 flex flex-col animate-slideIn z-40
        mt-14 md:mt-0
      `} data-testid="sidebar">
        <div className="p-6 border-b border-white/5 hidden md:block">
          <img 
            src="/images/noodly-logo.png" 
            alt="Noodly" 
            className="h-12 w-auto mb-3"
          />
          <h2 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk'}}>Pilote</h2>
          
          {/* Hierarchical attachment display */}
          {parentInfo && user.role === 'gestionnaire' && (
            <div className="mt-3 p-2 rounded bg-[#d03367]/10 border border-[#d03367]/30">
              <p className="text-xs text-gray-400">Rattaché au licencié :</p>
              <p className="text-sm font-semibold text-white">{parentInfo.name}</p>
            </div>
          )}
          
          {parentInfo && user.role === 'operateur' && (
            <div className="mt-3 p-2 rounded bg-[#d03367]/10 border border-[#d03367]/30">
              <p className="text-xs text-gray-400">Rattaché au gestionnaire :</p>
              <p className="text-sm font-semibold text-white">{parentInfo.name}</p>
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNav.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-[#d03367] text-white shadow-lg shadow-[#d03367]/20' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white hover:translate-x-1'
                }`}
                data-testid={`nav-${item.href.substring(1) || 'dashboard'}`}
                style={{animationDelay: `${index * 0.05}s`}}
              >
                <Icon size={20} />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 rounded-full bg-[#d03367] flex items-center justify-center font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-white">{user?.name}</p>
              <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
            </div>
          </div>
          {onRestartTour && (
            <Button
              onClick={() => {
                setMobileMenuOpen(false);
                onRestartTour();
              }}
              variant="outline"
              className="w-full bg-white/5 border-white/10 hover:bg-white/10 text-white mb-2"
              data-testid="restart-tour-button"
            >
              <HelpCircle size={16} className="mr-2" />
              Aide & Visite guidée
            </Button>
          )}
          <Button
            onClick={logout}
            variant="outline"
            className="w-full bg-white/5 border-white/10 hover:bg-white/10 text-white"
            data-testid="logout-button"
          >
            <LogOut size={16} className="mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto mt-14 md:mt-0">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
